import { GridColDef, GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium';
import { AsYouType } from 'libphonenumber-js';
import { isEmpty } from 'lodash';
import MuiPhoneNumber from 'material-ui-phone-number-2';
import React from 'react';
import PersonListTile from '../../common/components/person/PersonListTile';
import { FILTER_IDENTIFIERS } from '../../common/constants/constants';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import LookupGridField from '../../common/list/LookupGridField';
import {
    contactToOption,
    contactToUpdate,
    defaultEntityToOption,
    defaultFieldUpdater,
    defaultOptionRenderer,
    organizationToOption,
    organizationToUpdate,
    productionToOption,
    productionToUpdate,
    resourceLabel,
    resourceSubType,
    resourceType,
    valueGetter
} from '../opportunity/details/ContactListUtils';
import { termCreationExecutor } from '../termset/termset';

export const getColumns = (
    colDefs,
    setIsRowUpdated,
    afterSearch,
    getNonFuzzyQueryParamsForEmail,
    mutationObject,
    autoCreate,
    createNewOrg,
    filterSelectedOptions = undefined,
    disableFilterOptions = false
) => {
    const saveProduction = mutationObject['production'];
    const createTerm = mutationObject['createTerm'];
    const convertProductionRoleToArray = (value, row) => {
        const productionRoles = value ? [value] : [];
        return { ...row, productionRoles };
    };
    const convertRoleToArray = (value, row) => {
        const roles = value ? [value] : [];
        return { ...row, roles };
    };

    const rolesValueGetter = (value, row) => {
        return row && !isEmpty(value) ? value[0] : row.role;
    };

    const productionRolesValueGetter = (value, row) => {
        return row && !isEmpty(value) ? value[0] : row.productionRole;
    };

    const PhoneNumberCell = (props: GridRenderEditCellParams) => {
        const { id, value, field } = props;
        const apiRef = useGridApiContext();
        const asYouType = new AsYouType();

        const handleChange = (newValue: number | null) => {
            setIsRowUpdated && setIsRowUpdated(true);
            asYouType.input((newValue || value) as string);
            apiRef.current.setEditCellValue({
                id,
                field,
                value: asYouType.getNumber() ? asYouType.getNumber().number : asYouType.isValid() ? newValue : null
            });
        };

        return (
            <MuiPhoneNumber
                id={'Contact phone number'}
                defaultCountry={'us'}
                disableAreaCodes
                countryCodeEditable={false}
                autoFormat
                value={value}
                onChange={(value) => handleChange(value)}
                InputProps={{
                    size: 'small'
                }}
            />
        );
    };

    const renderPhoneNumberEditInputCell: GridColDef['renderCell'] = (params) => {
        return <PhoneNumberCell {...params} setIsRowUpdated={setIsRowUpdated} />;
    };

    const onAutoCreateProduction = (inputValue) => {
        return new Promise((resolve, reject) => {
            saveProduction({
                variables: {
                    input: {
                        publicTitle: inputValue
                    }
                }
            })
                .then((res) => {
                    const { autoCreateProduction } = res.data;
                    const updatedCreateOption = {
                        name: inputValue,
                        reference: autoCreateProduction.reference
                    };
                    resolve(updatedCreateOption);
                })
                .catch((err) => {
                    console.error('Failed to create term:', err);
                    reject(err);
                });
        });
    };

    const onAutoCreateTerm = termCreationExecutor(createTerm);

    const firstName: GridColDef = {
        field: 'name.firstName',
        headerName: 'First name',
        flex: 1,
        sortable: true,
        editable: true,
        minWidth: 120,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Person' }]}
                    OptionRenderer={PersonListTile}
                    toOption={contactToOption}
                    entityToUpdate={contactToUpdate}
                    filter={{ expressions: [{ field: 'public', value: { values: ['false'] } }] }}
                    filterOptions={disableFilterOptions ? undefined : filterSelectedOptions}
                />
            );
        }
    };

    const lastName: GridColDef = {
        field: 'name.lastName',
        headerName: 'Last name',
        flex: 1,
        sortable: true,
        editable: true,
        minWidth: 120,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Person' }]}
                    OptionRenderer={PersonListTile}
                    toOption={contactToOption}
                    entityToUpdate={contactToUpdate}
                    filter={{ expressions: [{ field: 'public', value: { values: ['false'] } }] }}
                    filterOptions={disableFilterOptions ? undefined : filterSelectedOptions}
                />
            );
        }
    };
    const organization: GridColDef = {
        field: 'organization',
        headerName: 'Organization',
        flex: 1,
        sortable: true,
        editable: true,
        type: 'string',
        minWidth: 80,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Organization' }]}
                    OptionRenderer={defaultOptionRenderer('organization')}
                    toOption={organizationToOption}
                    entityToUpdate={organizationToUpdate}
                    onAutoCreate={createNewOrg}
                    autoCreate={autoCreate}
                    filter={{ expressions: [{ field: 'inactive', value: { values: ['false'] } }] }}
                />
            );
        }
    };
    const email: GridColDef = {
        field: 'contactInfo.email.address',
        headerName: 'Email',
        flex: 0.75,
        sortable: true,
        editable: true,
        minWidth: 80,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Person' }]}
                    OptionRenderer={PersonListTile}
                    toOption={contactToOption}
                    entityToUpdate={contactToUpdate}
                    afterSearch={afterSearch}
                    getNonFuzzyQueryParams={getNonFuzzyQueryParamsForEmail}
                    filter={{ expressions: [{ field: 'public', value: { values: ['false'] } }] }}
                />
            );
        }
    };

    const phone: GridColDef = {
        field: 'contactInfo.phone.number',
        headerName: 'Phone',
        flex: 0.75,
        sortable: true,
        editable: true,
        minWidth: 80,
        valueGetter: valueGetter,
        renderEditCell: renderPhoneNumberEditInputCell
    };

    const title: GridColDef = {
        field: 'title',
        headerName: 'Title',
        flex: 0.75,
        sortable: true,
        editable: true,
        minWidth: 80,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Title' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={defaultFieldUpdater('name')}
                    onAutoCreate={onAutoCreateTerm}
                    autoCreate={autoCreate}
                />
            );
        }
    };
    const department: GridColDef = {
        field: 'organizationDepartment',
        headerName: 'Department',
        flex: 0.75,
        sortable: true,
        editable: true,
        minWidth: 80,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Department' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={defaultFieldUpdater('name')}
                    onAutoCreate={onAutoCreateTerm}
                    autoCreate={autoCreate}
                />
            );
        }
    };
    const role: GridColDef = {
        field: 'role',
        headerName: 'Professional Role',
        flex: 0.75,
        sortable: true,
        editable: true,
        minWidth: 80,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Role' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={defaultFieldUpdater('name')}
                    onAutoCreate={onAutoCreateTerm}
                    autoCreate={autoCreate}
                />
            );
        }
    };
    const production: GridColDef = {
        field: 'production',
        headerName: 'Production',
        flex: 1,
        sortable: true,
        editable: true,
        type: 'string',
        minWidth: 80,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Production' }]}
                    OptionRenderer={defaultOptionRenderer('production')}
                    toOption={productionToOption}
                    entityToUpdate={productionToUpdate}
                    onAutoCreate={onAutoCreateProduction}
                    autoCreate={autoCreate}
                />
            );
        }
    };
    const productionRole: GridColDef = {
        field: 'productionRole',
        headerName: 'Production Role',
        flex: 0.75,
        sortable: true,
        editable: true,
        type: 'string',
        minWidth: 80,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Role' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={defaultFieldUpdater('name')}
                    onAutoCreate={onAutoCreateTerm}
                    autoCreate={autoCreate}
                />
            );
        }
    };

    const productionDepartment: GridColDef = {
        field: 'productionDepartment',
        headerName: 'Production Department',
        flex: 0.75,
        sortable: true,
        editable: true,
        minWidth: 80,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Department' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={defaultFieldUpdater('name')}
                    onAutoCreate={onAutoCreateTerm}
                    autoCreate={autoCreate}
                />
            );
        }
    };

    const fullName: GridColDef = {
        field: 'fullName',
        headerName: 'Name',
        flex: 1,
        sortable: true,
        editable: true,
        minWidth: 120,
        valueGetter: (value, row) => {
            if (value) return value;
            const fn = row?.['name.firstName'] ?? row?.name?.firstName ?? row?._raw?.name?.firstName ?? '';
            const ln = row?.['name.lastName'] ?? row?.name?.lastName ?? row?._raw?.name?.lastName ?? '';
            return [fn, ln].filter(Boolean).join(' ');
        },
        renderEditCell: (params: GridRenderEditCellParams) => (
            <LookupGridField
                {...params}
                setIsRowUpdated={setIsRowUpdated}
                columns={colDefs}
                filters={[{ identifier: FILTER_IDENTIFIERS.entity, value: ENTITY_CONSTANTS.person }]}
                OptionRenderer={PersonListTile}
                toOption={contactToOption}
                entityToUpdate={contactToUpdate}
                filter={{ expressions: [{ field: 'public', value: { values: ['false'] } }] }}
                filterOptions={disableFilterOptions ? undefined : filterSelectedOptions}
            />
        )
    };

    const personReference: GridColDef = {
        field: 'personReference',
        headerName: 'Person Reference',
        flex: 1,
        sortable: true,
        editable: true,
        hideable: false,
        valueGetter: valueGetter
    };

    const productionReference: GridColDef = {
        field: 'productionReference',
        headerName: 'Production Reference',
        flex: 1,
        sortable: true,
        editable: true,
        hideable: false,
        valueGetter: valueGetter
    };

    const organizationReference: GridColDef = {
        field: 'organizationReference',
        headerName: 'Organization Reference',
        flex: 1,
        sortable: true,
        editable: true,
        hideable: false,
        valueGetter: valueGetter
    };

    const productionRoles: GridColDef = {
        field: 'productionRoles',
        headerName: 'Production Roles',
        flex: 0.75,
        sortable: true,
        editable: true,
        type: 'string',
        minWidth: 80,
        valueGetter: productionRolesValueGetter,
        valueSetter: convertProductionRoleToArray,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Role' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={defaultFieldUpdater('name')}
                    onAutoCreate={onAutoCreateTerm}
                    autoCreate={autoCreate}
                />
            );
        }
    };

    const roles: GridColDef = {
        field: 'roles',
        headerName: 'Professional Roles',
        flex: 0.75,
        sortable: true,
        editable: true,
        minWidth: 80,
        valueGetter: rolesValueGetter,
        valueSetter: convertRoleToArray,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Role' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={defaultFieldUpdater('name')}
                    onAutoCreate={onAutoCreateTerm}
                    autoCreate={autoCreate}
                />
            );
        }
    };

    const resourceTypeCol: GridColDef = {
        ...resourceType,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'ResourceType' }]}
                    filter={{ expressions: [{ field: 'labels', value: { values: ['Individual'] } }] }}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={defaultFieldUpdater('name')}
                    allowFreeText={false}
                />
            );
        }
    };

    const resourceSubTypeCol: GridColDef = {
        ...resourceSubType,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    setIsRowUpdated={setIsRowUpdated}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'CatalogSubType' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={defaultFieldUpdater('name')}
                />
            );
        }
    };

    const resourceLabelCol: GridColDef = {
        ...resourceLabel,
        valueSetter: (value, row) => {
            setIsRowUpdated && setIsRowUpdated(true);
            return { ...row, resourceLabel: value };
        }
    };

    const columnMap = {
        fullName,
        'name.firstName': firstName,
        'name.lastName': lastName,
        organization,
        'contactInfo.email.address': email,
        'contactInfo.phone.number': phone,
        title,
        organizationDepartment: department,
        role,
        roles,
        production,
        productionRole,
        productionRoles,
        productionDepartment,
        resourceLabel: resourceLabelCol,
        resourceType: resourceTypeCol,
        resourceSubType: resourceSubTypeCol,
        personReference,
        productionReference,
        organizationReference
    };

    const cols = colDefs.map((colDef) => columnMap[colDef.field]).filter((col) => col !== undefined);

    const configureHiddenOrImmutableColumns = () => {
        const immutableColumns = colDefs.filter((col) => !col.editable).map((col) => col.field);
        const hiddenColumns = colDefs.filter((col) => col.hide).map((col) => col.field);

        immutableColumns.forEach((fieldName) => {
            const col = cols.find((col) => col.field === fieldName);
            if (col) {
                col.editable = false;
            }
        });

        hiddenColumns.forEach((fieldName) => {
            const col = cols.find((col) => col.field === fieldName);
            if (col) {
                col.hide = true;
            }
        });

        return cols;
    };

    const updatedColumns = configureHiddenOrImmutableColumns();

    return updatedColumns;
};
